import {
  withAuthInfo,
  useRedirectFunctions,
  useLogoutFunction,
  WithAuthInfoProps,
  useHostedPageUrls,
} from "@propelauth/react";
import { useAuthFrontendApis } from "@propelauth/frontend-apis-react";
import { getAuthUrl, getPropelAPI } from "@fuse/utils/environement";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Typography,
  Link,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  Grid,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import { styled } from "@mui/material/styles";
import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import Logo from "../../theme-layouts/shared-components/LogoDark";
import LanguageSwitcher from "../../theme-layouts/shared-components/LanguageSwitcher";
import {
  generatePasswordForSignup,
  isPasswordCommon,
  COMMON_PASSWORDS,
} from "./utils/passwordGenerator";
import { Snackbar } from "@mui/material";
import Enzoic from "@enzoic/enzoic-react-password-strength";

const StyledBox = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "100vh",
  padding: theme.spacing(2),
  backgroundColor: "#f7f7f7",
}));

const FormContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: 500,
  padding: theme.spacing(4),
  borderRadius: "1rem",
  backgroundColor: "#ffffff",
  border: "1px solid #e0e0e0",
  boxShadow: "none",
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  display: "flex",
  justifyContent: "center",
  "& img": {
    maxHeight: 80,
    width: "auto",
  },
}));

interface SignUpFormData {
  email: string;
  password: string;
  confirmPassword: string;
  username: string;
  firstName: string;
  lastName: string;
  hotelName: string;
  hotelDomain: string;
}

const SignUpModule = withAuthInfo((props: WithAuthInfoProps) => {
  const { t } = useTranslation("signUpPage");
  const logoutFunction = useLogoutFunction();
  const { redirectToLoginPage } = useRedirectFunctions();
  const { getSignupPageUrl } = useHostedPageUrls();
  // Note: Using redirect to PropelAuth hosted signup page since emailPasswordSignup is not available
  // const { emailPasswordSignup } = useAuthFrontendApis();
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState<string>("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [enzoicResult, setEnzoicResult] = useState<{
    score: number;
    feedback: string[];
  }>({ score: 0, feedback: [] });

  // Parse invitation token from URL
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite_token");
  const [inviteData, setInviteData] = useState<any>(null);
  const [isInvitation, setIsInvitation] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<SignUpFormData>({
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
      username: "",
      firstName: "",
      lastName: "",
      hotelName: "",
      hotelDomain: "",
    },
  });

  const watchedPassword = watch("password");
  const watchedConfirmPassword = watch("confirmPassword");
  const watchedEmail = watch("email");
  const watchedFirstName = watch("firstName");
  const watchedLastName = watch("lastName");
  const watchedUsername = watch("username");
  const watchedHotelName = watch("hotelName");
  const watchedHotelDomain = watch("hotelDomain");

  // Use enhanced common passwords list from utility
  const commonPasswords = COMMON_PASSWORDS;

  // Enzoic callback to handle password strength updates
  const handleEnzoicChange = (result: any) => {
    console.log("Enzoic result:", result); // Debug logging
    setEnzoicResult({
      score: result.score || 0,
      feedback: result.feedback || [],
    });
  };

  // Update Enzoic analysis when password changes
  useEffect(() => {
    if (!watchedPassword) {
      setEnzoicResult({ score: 0, feedback: [] });
    }
    // The Enzoic component should pick up the password change via inputProps.value
  }, [watchedPassword]);

  // Enhanced password validation using Enzoic state
  const validatePasswordRequirements = (password: string) => {
    const basicRequirements = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      numbers: /\d/.test(password),
      symbols: /[^A-Za-z0-9]/.test(password),
      noСyrillic:
        !/[\u0400-\u04FF\u0500-\u052F\u2DE0-\u2DFF\uA640-\uA69F]/.test(
          password
        ),
      notCommon: !isPasswordCommon(password),
    };

    // Check if password meets all basic requirements
    const meetsBasicRequirements =
      Object.values(basicRequirements).every(Boolean);

    // For strong passwords (16+ chars with all requirements), be more lenient with Enzoic
    const isStrongPassword = password.length >= 16 && meetsBasicRequirements;

    return {
      ...basicRequirements,
      enzoicScore: enzoicResult.score,
      enzoicPasses: isStrongPassword ? true : enzoicResult.score >= 1, // Bypass Enzoic for strong passwords
      enzoicFeedback: enzoicResult.feedback,
    };
  };

  // Get missing requirements for error messages (simplified)
  const getMissingRequirements = (password: string): string[] => {
    if (!password) return [];

    const requirements = validatePasswordRequirements(password);
    const missing: string[] = [];

    // Only show critical missing requirements
    const basicRequirementsMet =
      requirements.length &&
      requirements.uppercase &&
      requirements.lowercase &&
      requirements.numbers &&
      requirements.symbols &&
      requirements.noСyrillic;

    if (!basicRequirementsMet) {
      missing.push(t("PASSWORD_BASIC_REQUIREMENTS"));
    }

    // For strong passwords (16+ chars), don't require Enzoic validation
    const isStrongPassword = password.length >= 16 && basicRequirementsMet;
    if (!requirements.enzoicPasses && !isStrongPassword) {
      missing.push(t("PASSWORD_NEEDS_IMPROVEMENT"));
    }

    return missing;
  };

  // Enhanced password strength calculation using Enzoic state
  const calculatePasswordStrength = (
    password: string
  ): { score: number; level: string; color: string; feedback: string[] } => {
    if (!password)
      return { score: 0, level: "WEAK", color: "#f44336", feedback: [] };

    const requirements = validatePasswordRequirements(password);
    const enzoicScore = enzoicResult.score || 0;
    const enzoicFeedback = enzoicResult.feedback || [];

    // Check if all mandatory requirements are met
    const mandatoryRequirementsMet =
      requirements.length &&
      requirements.uppercase &&
      requirements.lowercase &&
      requirements.numbers &&
      requirements.symbols &&
      requirements.noСyrillic &&
      requirements.enzoicPasses; // Include Enzoic validation (with bypass for strong passwords)

    // If mandatory requirements aren't met, force weak rating
    if (!mandatoryRequirementsMet) {
      return {
        score: Math.min(25, password.length * 2),
        level: "WEAK",
        color: "#f44336",
        feedback: enzoicFeedback,
      };
    }

    // Check if this is a strong password (16+ chars with all requirements)
    const isStrongPassword = password.length >= 16 && mandatoryRequirementsMet;

    // Use Enzoic score as primary measure, but boost strong passwords
    let score = (enzoicScore / 4) * 100;

    // For strong passwords, ensure minimum good score
    if (isStrongPassword) {
      score = Math.max(score, 85); // Strong passwords get at least 85% score
    }

    // Apply additional penalties/bonuses based on our requirements
    if (!requirements.notCommon) score = Math.min(score, 25);
    if (requirements.noСyrillic === false) score = Math.min(score, 30);

    // Ensure score is within bounds
    score = Math.max(0, Math.min(100, score));

    // Determine level and color - boost strong passwords
    let level: string;
    let color: string;

    if (isStrongPassword) {
      level = "STRONG";
      color = "#4caf50";
    } else if (enzoicScore === 0) {
      level = "WEAK";
      color = "#f44336";
    } else if (enzoicScore === 1) {
      level = "FAIR";
      color = "#ff9800";
    } else if (enzoicScore === 2) {
      level = "GOOD";
      color = "#fdd835";
    } else if (enzoicScore >= 3) {
      level = "STRONG";
      color = "#4caf50";
    } else {
      level = "WEAK";
      color = "#f44336";
    }

    return { score, level, color, feedback: enzoicFeedback };
  };

  // Calculate password strength for current password
  const passwordStrength = calculatePasswordStrength(watchedPassword);

  // Real-time password match validation
  const passwordsMatch =
    watchedPassword &&
    watchedConfirmPassword &&
    watchedPassword === watchedConfirmPassword;
  const passwordMismatch =
    watchedConfirmPassword &&
    watchedPassword &&
    watchedPassword !== watchedConfirmPassword;
  const hasConfirmPasswordError = Boolean(
    passwordMismatch || (!!errors.confirmPassword && watchedConfirmPassword)
  );

  // Step 1 validation check with enhanced Enzoic validation
  const isStep1Valid = () => {
    if (!watchedPassword) return false;

    const requirements = validatePasswordRequirements(watchedPassword);
    const allPasswordRequirementsMet =
      requirements.length &&
      requirements.uppercase &&
      requirements.lowercase &&
      requirements.numbers &&
      requirements.symbols &&
      requirements.noСyrillic &&
      requirements.notCommon &&
      requirements.enzoicPasses; // Must pass Enzoic validation

    return (
      watchedFirstName?.trim().length >= 2 &&
      watchedLastName?.trim().length >= 2 &&
      watchedUsername?.trim().length >= 3 &&
      /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(watchedEmail || "") &&
      allPasswordRequirementsMet &&
      watchedConfirmPassword?.length >= 8 &&
      passwordsMatch &&
      !errors.firstName &&
      !errors.lastName &&
      !errors.username &&
      !errors.email &&
      !errors.password &&
      !errors.confirmPassword
    );
  };

  // Step 2 validation check (for non-invitation flows)
  const isStep2Valid = () => {
    if (isInvitation) return true;
    return (
      watchedHotelName?.trim().length >= 2 &&
      watchedHotelDomain?.trim().length >= 3
    );
  };

  // Parse invitation token if present
  useEffect(() => {
    if (inviteToken) {
      try {
        // Decode the base64 token
        const decodedToken = atob(inviteToken);
        const tokenData = JSON.parse(decodedToken);
        setInviteData(tokenData);
        setIsInvitation(true);

        // Pre-fill email if provided in token
        if (tokenData.email) {
          setValue("email", tokenData.email);
        }

        console.log("Invitation data:", tokenData);
      } catch (error) {
        console.error("Error parsing invitation token:", error);
        setAuthError(
          "Invalid invitation link. Please contact the administrator."
        );
      }
    }
  }, [inviteToken, setValue]);

  // Auto-fill hotel domain from email when on step 1 (only for new signups)
  useEffect(() => {
    if (activeStep === 1 && !isInvitation) {
      const email = watchedEmail;
      if (email && email.includes("@")) {
        const domain = email.split("@")[1];
        setValue("hotelDomain", domain);
      }
    }
  }, [activeStep, watchedEmail, setValue, isInvitation]);

  // Clear hotel name when switching to step 2 (ensure it's empty)
  useEffect(() => {
    if (activeStep === 1 && !isInvitation) {
      setValue("hotelName", "");
    }
  }, [activeStep, isInvitation, setValue]);

  const handleLogOut = () => {
    logoutFunction(true);
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleGeneratePassword = () => {
    try {
      const newPassword = generatePasswordForSignup();
      setValue("password", newPassword);
      setValue("confirmPassword", newPassword);
      setSnackbarMessage(t("PASSWORD_GENERATED"));
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error generating password:", error);
      setSnackbarMessage("Error generating password");
      setSnackbarOpen(true);
    }
  };

  // Removed copy password functionality to fix button spacing

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Map Enzoic feedback to internationalized messages
  const mapEnzoicFeedback = (feedback: string[]): string[] => {
    const mappedFeedback: string[] = [];

    feedback.forEach((item) => {
      const feedbackLower = item.toLowerCase();

      if (
        feedbackLower.includes("dictionary") ||
        feedbackLower.includes("common")
      ) {
        mappedFeedback.push(t("ENZOIC_WEAKNESS_DICTIONARY"));
      } else if (
        feedbackLower.includes("predictable") ||
        feedbackLower.includes("pattern")
      ) {
        mappedFeedback.push(t("ENZOIC_WEAKNESS_PREDICTABLE"));
      } else if (
        feedbackLower.includes("repetitive") ||
        feedbackLower.includes("repeat")
      ) {
        mappedFeedback.push(t("ENZOIC_WEAKNESS_REPETITIVE"));
      } else if (
        feedbackLower.includes("sequence") ||
        feedbackLower.includes("sequential")
      ) {
        mappedFeedback.push(t("ENZOIC_WEAKNESS_SEQUENCE"));
      } else if (feedbackLower.includes("keyboard")) {
        mappedFeedback.push(t("ENZOIC_WEAKNESS_KEYBOARD"));
      } else if (feedbackLower.includes("date")) {
        mappedFeedback.push(t("ENZOIC_WEAKNESS_DATE"));
      } else if (feedbackLower.includes("personal")) {
        mappedFeedback.push(t("ENZOIC_WEAKNESS_PERSONAL"));
      } else if (
        feedbackLower.includes("compromised") ||
        feedbackLower.includes("breach")
      ) {
        mappedFeedback.push(t("ENZOIC_WEAKNESS_COMPROMISED"));
      } else {
        // If we can't map it, show the original feedback
        mappedFeedback.push(item);
      }
    });

    return mappedFeedback;
  };

  const mapErrorMessage = (error: any): string => {
    const errorMessage = error?.message?.toLowerCase() || "";
    const errorCode = error?.code || error?.error_code || "";
    const errorStatus = error?.status || 0;

    console.log("Error details:", {
      errorMessage,
      errorCode,
      errorStatus,
      fullError: error,
    });

    // Handle specific password error codes
    if (errorMessage === "password_too_common") {
      return t("PASSWORD_TOO_COMMON");
    }

    if (errorMessage === "password_too_weak") {
      return t("PASSWORD_TOO_WEAK");
    }

    // Network and server errors
    if (
      errorMessage.includes("network") ||
      errorMessage.includes("connection") ||
      errorCode === "network_error" ||
      errorMessage.includes("fetch")
    ) {
      return t("NETWORK_ERROR");
    }

    if (errorMessage.includes("timeout") || errorCode === "timeout") {
      return t("CONNECTION_TIMEOUT");
    }

    if (
      errorMessage.includes("server_error") ||
      errorMessage.includes("internal_server_error") ||
      errorCode === "server_error" ||
      errorStatus >= 500
    ) {
      return t("SERVER_ERROR");
    }

    if (
      errorMessage.includes("service_unavailable") ||
      errorMessage.includes("maintenance") ||
      errorCode === "service_unavailable" ||
      errorStatus === 503
    ) {
      return t("SERVICE_UNAVAILABLE");
    }

    // PropelAuth specific errors
    if (
      errorMessage.includes("email already exists") ||
      errorMessage.includes("email_already_exists") ||
      errorCode === "email_already_exists" ||
      errorMessage.includes("user already exists") ||
      errorMessage.includes("user with this email") ||
      (errorMessage.includes("duplicate") && errorMessage.includes("email")) ||
      (errorStatus === 409 && errorMessage.includes("email"))
    ) {
      return t("EMAIL_ALREADY_EXISTS");
    }

    if (
      errorMessage.includes("username already exists") ||
      errorMessage.includes("username_already_exists") ||
      errorCode === "username_already_exists" ||
      errorMessage.includes("username is taken") ||
      (errorMessage.includes("duplicate") &&
        errorMessage.includes("username")) ||
      (errorStatus === 409 && errorMessage.includes("username"))
    ) {
      return t("USERNAME_ALREADY_EXISTS");
    }

    if (
      errorMessage.includes("organization already exists") ||
      errorMessage.includes("org_already_exists") ||
      errorMessage.includes("hotel already exists") ||
      errorMessage.includes("name already exists") ||
      errorMessage.includes("organization name") ||
      (errorMessage.includes("duplicate") && errorMessage.includes("name")) ||
      (errorStatus === 409 && errorMessage.includes("organization"))
    ) {
      return t("HOTEL_NAME_ALREADY_EXISTS");
    }

    if (
      errorMessage.includes("domain already exists") ||
      errorMessage.includes("domain_already_exists") ||
      errorMessage.includes("domain already in use") ||
      (errorMessage.includes("duplicate") && errorMessage.includes("domain")) ||
      (errorStatus === 409 && errorMessage.includes("domain"))
    ) {
      return t("HOTEL_DOMAIN_ALREADY_EXISTS");
    }

    if (
      errorMessage.includes("password too weak") ||
      errorMessage.includes("password_too_weak") ||
      errorCode === "password_too_weak" ||
      errorMessage.includes("password does not meet")
    ) {
      return t("PASSWORD_TOO_WEAK");
    }

    if (
      errorMessage.includes("too commonly used") ||
      errorMessage.includes("commonly used") ||
      errorMessage.includes("common password") ||
      errorCode === "password_too_common"
    ) {
      return t("PASSWORD_TOO_COMMON");
    }

    if (
      errorMessage.includes("invalid email") ||
      errorMessage.includes("invalid_email") ||
      errorCode === "invalid_email" ||
      errorMessage.includes("malformed email")
    ) {
      return t("EMAIL_INVALID");
    }

    if (
      errorMessage.includes("account disabled") ||
      errorCode === "account_disabled"
    ) {
      return t("ACCOUNT_DISABLED");
    }

    if (
      errorMessage.includes("account locked") ||
      errorCode === "account_locked"
    ) {
      return t("ACCOUNT_LOCKED");
    }

    if (
      errorMessage.includes("bad request") ||
      errorMessage.includes("invalid request") ||
      errorMessage.includes("validation failed") ||
      errorStatus === 400
    ) {
      return t("BAD_REQUEST");
    }

    // Fallback for any 4xx client errors
    if (errorStatus >= 400 && errorStatus < 500) {
      return t("BAD_REQUEST");
    }

    // Default fallback for unexpected errors
    console.warn("Unmapped error:", error);
    return t("UNKNOWN_ERROR");
  };

  const onSubmit = async (data: SignUpFormData) => {
    setLoading(true);
    setAuthError("");

    try {
      // Create user using PropelAuth API
      const userResponse = await fetch(`${getAuthUrl()}/api/backend/v1/user/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getPropelAPI()}`,
        },
        body: JSON.stringify({
          email: data.email,
          email_confirmed: true,
          send_email_to_confirm_email_address: false,
          ignore_domain_restrictions: false,
          password: data.password,
          ask_user_to_update_password_on_login: false,
          username: data.username,
          first_name: data.firstName,
          last_name: data.lastName,
          properties: {
            chosen_language: "bg",
          },
        }),
      });

      if (!userResponse.ok) {
        const errorData = await userResponse.json();

        // Handle specific password validation errors from Propel
        if (errorData.password && Array.isArray(errorData.password)) {
          const passwordError = errorData.password[0];
          if (passwordError.includes("too commonly used")) {
            throw new Error("PASSWORD_TOO_COMMON");
          }
          if (passwordError.includes("too weak")) {
            throw new Error("PASSWORD_TOO_WEAK");
          }
          throw new Error(passwordError);
        }

        throw new Error(errorData.message || "Failed to create user");
      }

      const userData = await userResponse.json();
      console.log("User created:", userData);

      let orgId: string;

      if (isInvitation && inviteData) {
        // For invitations, use the org_id from the token
        orgId = inviteData.org_id;
        console.log("Using invitation org_id:", orgId);
      } else {
        // For new signups, create organization
        const orgResponse = await fetch(`${getAuthUrl()}/api/backend/v1/org/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getPropelAPI()}`,
          },
          body: JSON.stringify({
            name: data.hotelName,
            autojoin_by_domain: true,
            members_must_have_matching_domain: false,
            max_users: 100,
          }),
        });

        if (!orgResponse.ok) {
          const errorData = await orgResponse.json();
          throw new Error(errorData.message || "Failed to create organization");
        }

        const orgData = await orgResponse.json();
        console.log("Organization created:", orgData);
        orgId = orgData.orgId || orgData.org_id || orgData.id;
      }

      // Add user to organization
      console.log("userData.user_id:", userData.user_id);
      console.log("orgId:", orgId);

      const userId = userData.userId || userData.user_id || userData.id;

      const addUserPayload = {
        user_id: userId,
        org_id: orgId,
        role: isInvitation ? inviteData?.role || "Cleaner" : "Admin",
      };
      console.log("Adding user to org payload:", addUserPayload);

      // Validate that we have the required fields
      if (!userId) {
        throw new Error("User ID not found in user creation response");
      }
      if (!orgId) {
        throw new Error(
          "Organization ID not found in organization creation response"
        );
      }

      console.log(
        "Making request to:",
        `${getAuthUrl()}/api/backend/v1/org/add_user`
      );
      console.log("API Key length:", getPropelAPI()?.length || 0);
      console.log(
        "API Key starts with:",
        getPropelAPI()?.substring(0, 10) + "..."
      );
      console.log("Request headers:", {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getPropelAPI()}`,
      });

      const addUserResponse = await fetch(
        `${getAuthUrl()}/api/backend/v1/org/add_user`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getPropelAPI()}`,
            Accept: "application/json",
          },
          body: JSON.stringify(addUserPayload),
        }
      );

      if (!addUserResponse.ok) {
        const errorData = await addUserResponse.json();
        console.error("Add user to org failed:", errorData);
        console.error("Response status:", addUserResponse.status);
        throw new Error(
          errorData.message || "Failed to add user to organization"
        );
      }

      // Redirect to home page
      window.location.href = "/";
    } catch (error) {
      console.error("Sign up error:", error);
      const errorMsg = mapErrorMessage(error as any);
      setAuthError(errorMsg);

      // If it's a password-related error, show snackbar as well
      if (
        errorMsg.includes(t("PASSWORD_TOO_COMMON")) ||
        errorMsg.includes(t("PASSWORD_TOO_WEAK"))
      ) {
        setSnackbarMessage(errorMsg);
        setSnackbarOpen(true);
      }
    } finally {
      setLoading(false);
    }
  };

  if (props.isLoggedIn) {
    return (
      <StyledBox>
        <FormContainer>
          <Typography variant="h5" component="h1" gutterBottom align="center">
            Welcome, {props.user.username}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              onClick={() => (window.location.href = "/")}
            >
              Go to Dashboard
            </Button>
            <Button
              variant="outlined"
              color="error"
              fullWidth
              size="large"
              onClick={handleLogOut}
            >
              Logout
            </Button>
          </Box>
        </FormContainer>
      </StyledBox>
    );
  }

  const steps = isInvitation
    ? [t("STEP_PERSONAL_INFO")]
    : [t("STEP_PERSONAL_INFO"), t("STEP_HOTEL_INFO")];

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="firstName"
                  control={control}
                  rules={{
                    required: t("FIRST_NAME_REQUIRED"),
                    minLength: {
                      value: 2,
                      message: t("FIRST_NAME_MIN_LENGTH"),
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t("FIRST_NAME_LABEL")}
                      fullWidth
                      error={!!errors.firstName}
                      helperText={errors.firstName?.message}
                      disabled={loading}
                      size="small"
                      required
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="lastName"
                  control={control}
                  rules={{
                    required: t("LAST_NAME_REQUIRED"),
                    minLength: {
                      value: 2,
                      message: t("LAST_NAME_MIN_LENGTH"),
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t("LAST_NAME_LABEL")}
                      fullWidth
                      error={!!errors.lastName}
                      helperText={errors.lastName?.message}
                      disabled={loading}
                      size="small"
                      required
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Controller
              name="username"
              control={control}
              rules={{
                required: t("USERNAME_REQUIRED"),
                minLength: {
                  value: 3,
                  message: t("USERNAME_MIN_LENGTH"),
                },
                pattern: {
                  value: /^[a-zA-Z0-9_]+$/,
                  message: t("USERNAME_INVALID"),
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("USERNAME_LABEL")}
                  fullWidth
                  error={!!errors.username}
                  helperText={errors.username?.message}
                  disabled={loading}
                  size="small"
                  required
                />
              )}
            />

            <Controller
              name="email"
              control={control}
              rules={{
                required: t("EMAIL_REQUIRED"),
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: t("EMAIL_INVALID"),
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("EMAIL_LABEL")}
                  type="email"
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  disabled={loading}
                  size="small"
                  required
                />
              )}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="password"
                  control={control}
                  rules={{
                    required: t("PASSWORD_REQUIRED"),
                    minLength: {
                      value: 8,
                      message: t("PASSWORD_MIN_LENGTH"),
                    },
                    validate: (value) => {
                      if (!value) return t("PASSWORD_REQUIRED");

                      const missing = getMissingRequirements(value);
                      if (missing.length > 0) {
                        return missing.join(", ");
                      }

                      return true;
                    },
                  }}
                  render={({ field }) => (
                    <Box>
                      <TextField
                        {...field}
                        label={t("PASSWORD_LABEL")}
                        type={showPassword ? "text" : "password"}
                        fullWidth
                        error={!!errors.password}
                        helperText={errors.password?.message}
                        disabled={loading}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip title={t("GENERATE_PASSWORD")}>
                                <IconButton
                                  aria-label="generate password"
                                  onClick={handleGeneratePassword}
                                  sx={{
                                    color: "#666666",
                                    "&:hover": {
                                      color: "#006783",
                                    },
                                  }}
                                >
                                  <FuseSvgIcon size={20}>
                                    heroicons-outline:key
                                  </FuseSvgIcon>
                                </IconButton>
                              </Tooltip>
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleTogglePasswordVisibility}
                                edge="end"
                                sx={{
                                  color: "#666666",
                                  "&:hover": {
                                    color: "#006783",
                                  },
                                }}
                              >
                                {showPassword ? (
                                  <FuseSvgIcon size={20}>
                                    material-outline:visibility_off
                                  </FuseSvgIcon>
                                ) : (
                                  <FuseSvgIcon size={20}>
                                    material-outline:visibility
                                  </FuseSvgIcon>
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        size="small"
                        required
                      />

                      {/* Hidden Enzoic component for password strength analysis */}
                      <Box
                        sx={{
                          position: "absolute",
                          left: "-9999px",
                          top: "-9999px",
                        }}
                      >
                        <Enzoic
                          minLength={8}
                          minScore={0}
                          scoreWords={[
                            "very weak",
                            "weak",
                            "fair",
                            "good",
                            "strong",
                          ]}
                          changeCallback={handleEnzoicChange}
                          inputProps={{
                            value: watchedPassword || "",
                            onChange: () => {}, // Value is controlled by watchedPassword
                          }}
                        />
                      </Box>

                      {/* Password Strength Indicator */}
                      {watchedPassword && (
                        <Box sx={{ mt: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {t("PASSWORD_STRENGTH_LABEL")}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: passwordStrength.color,
                                fontWeight: 600,
                              }}
                            >
                              {t(`PASSWORD_STRENGTH_${passwordStrength.level}`)}
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={passwordStrength.score}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              backgroundColor: "rgba(0,0,0,0.1)",
                              "& .MuiLinearProgress-bar": {
                                backgroundColor: passwordStrength.color,
                                borderRadius: 3,
                              },
                            }}
                          />

                          {/* Simplified Password Tips */}
                          <Tooltip
                            title={
                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 600,
                                    display: "block",
                                    mb: 0.5,
                                  }}
                                >
                                  {t("PASSWORD_TIPS_TITLE")}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ display: "block", fontSize: "0.7rem" }}
                                >
                                  {t("PASSWORD_TIP_GENERATE")}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ display: "block", fontSize: "0.7rem" }}
                                >
                                  {t("PASSWORD_TIP_AVOID_COMMON")}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ display: "block", fontSize: "0.7rem" }}
                                >
                                  {t("PASSWORD_TIP_MIX_CHARS")}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ display: "block", fontSize: "0.7rem" }}
                                >
                                  {t("PASSWORD_TIP_UNIQUE")}
                                </Typography>
                              </Box>
                            }
                            placement="top"
                            arrow
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: "primary.main",
                                cursor: "help",
                                textDecoration: "underline",
                                fontSize: "0.7rem",
                                mt: 0.5,
                                display: "inline-block",
                              }}
                            >
                              {t("PASSWORD_TIPS_HELP")}
                            </Typography>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="confirmPassword"
                  control={control}
                  rules={{
                    required: t("CONFIRM_PASSWORD_REQUIRED"),
                    validate: (value) =>
                      value === watchedPassword || t("PASSWORDS_DONT_MATCH"),
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t("CONFIRM_PASSWORD_LABEL")}
                      type={showConfirmPassword ? "text" : "password"}
                      fullWidth
                      error={hasConfirmPasswordError}
                      helperText={
                        errors.confirmPassword?.message ||
                        (passwordMismatch ? t("PASSWORDS_DONT_MATCH") : "")
                      }
                      disabled={loading}
                      color={
                        passwordsMatch &&
                        watchedConfirmPassword &&
                        !errors.confirmPassword
                          ? "success"
                          : "primary"
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {passwordsMatch &&
                              watchedConfirmPassword &&
                              !errors.confirmPassword && (
                                <FuseSvgIcon
                                  size={20}
                                  sx={{
                                    color: "success.main",
                                    mr: 1,
                                  }}
                                >
                                  heroicons-outline:check-circle
                                </FuseSvgIcon>
                              )}
                            {hasConfirmPasswordError && (
                              <FuseSvgIcon
                                size={20}
                                sx={{
                                  color: "error.main",
                                  mr: 1,
                                }}
                              >
                                heroicons-outline:x-circle
                              </FuseSvgIcon>
                            )}
                            <IconButton
                              aria-label="toggle confirm password visibility"
                              onClick={handleToggleConfirmPasswordVisibility}
                              edge="end"
                              sx={{
                                color: "#666666",
                                "&:hover": {
                                  color: "#006783",
                                },
                              }}
                            >
                              {showConfirmPassword ? (
                                <FuseSvgIcon size={20}>
                                  material-outline:visibility_off
                                </FuseSvgIcon>
                              ) : (
                                <FuseSvgIcon size={20}>
                                  material-outline:visibility
                                </FuseSvgIcon>
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      size="small"
                      required
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Controller
              name="hotelName"
              control={control}
              rules={{
                required: t("HOTEL_NAME_REQUIRED"),
                minLength: {
                  value: 2,
                  message: t("HOTEL_NAME_MIN_LENGTH"),
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("HOTEL_NAME_LABEL")}
                  fullWidth
                  error={!!errors.hotelName}
                  helperText={errors.hotelName?.message}
                  disabled={loading}
                  size="small"
                  required
                />
              )}
            />

            <Controller
              name="hotelDomain"
              control={control}
              rules={{
                required: t("HOTEL_DOMAIN_REQUIRED"),
                pattern: {
                  value:
                    /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}$/,
                  message: t("HOTEL_DOMAIN_INVALID"),
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("HOTEL_DOMAIN_LABEL")}
                  placeholder="example.com"
                  fullWidth
                  error={!!errors.hotelDomain}
                  helperText={
                    errors.hotelDomain?.message ||
                    "Domain is automatically filled from your email"
                  }
                  disabled={true} // Make it appear read-only
                  size="small"
                  required
                  sx={{
                    "& .MuiInputBase-input": {
                      color: "text.primary",
                      fontWeight: 500,
                      backgroundColor: "transparent",
                    },
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "action.hover",
                      "&.Mui-disabled": {
                        backgroundColor: "action.disabledBackground",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "divider",
                        },
                        "& .MuiInputBase-input": {
                          color: "text.disabled",
                          "-webkit-text-fill-color": "rgba(0, 0, 0, 0.6)",
                        },
                      },
                    },
                  }}
                />
              )}
            />
          </Box>
        );

      default:
        return null;
    }
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStepSubmit = () => {
    if (activeStep === steps.length - 1) {
      handleSubmit(onSubmit)();
    } else {
      // Only allow next if current step is valid
      if (activeStep === 0) {
        if (!isStep1Valid()) {
          // Trigger validation for all fields to show errors
          handleSubmit(() => {})();
          return; // Prevent navigation if step 1 is invalid
        }
      }
      if (activeStep === 1 && !isStep2Valid()) {
        // Trigger validation for hotel fields
        handleSubmit(() => {})();
        return; // Prevent navigation if step 2 is invalid
      }
      handleNext();
    }
  };

  return (
    <StyledBox>
      <Box sx={{ position: "absolute", top: 16, right: 16 }}>
        <LanguageSwitcher />
      </Box>

      <LogoContainer>
        <Logo />
      </LogoContainer>

      <Typography
        variant="h2"
        component="h1"
        gutterBottom
        align="center"
        sx={{
          fontWeight: "bold",
          fontSize: "2rem",
          color: "#0f0f0f",
          mb: 3,
        }}
      >
        {isInvitation ? "Join Organization" : t("SIGN_UP_TITLE")}
      </Typography>

      <FormContainer>
        {authError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {authError}
          </Alert>
        )}

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ width: "100%" }}>
          {renderStepContent(activeStep)}

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{ mr: 1 }}
            >
              {t("BACK")}
            </Button>
            <Button
              variant="contained"
              onClick={handleStepSubmit}
              disabled={
                loading ||
                (activeStep === 0 && !isStep1Valid()) ||
                (activeStep === 1 && !isStep2Valid())
              }
              sx={{
                backgroundColor: "#006783",
                "&:hover": {
                  backgroundColor: "#005366",
                },
                "&:disabled": {
                  backgroundColor: "#cccccc",
                },
              }}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1, color: "white" }} />
                  {t("SIGNING_UP")}
                </>
              ) : activeStep === steps.length - 1 ? (
                isInvitation ? (
                  "Join Organization"
                ) : (
                  t("SIGN_UP_BUTTON")
                )
              ) : (
                t("NEXT")
              )}
            </Button>
          </Box>
        </Box>
      </FormContainer>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 1,
          mt: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t("HAVE_ACCOUNT")}
        </Typography>
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={() => redirectToLoginPage()}
          sx={{
            textDecoration: "none",
            color: "#006783",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          {t("SIGN_IN_LINK")}
        </Link>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </StyledBox>
  );
});

function SignUpPage() {
  return <SignUpModule />;
}

export default SignUpPage;
